import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, Filter } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  pending_approval: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  sent_to_supplier: { label: 'Sent to Supplier', color: 'bg-blue-100 text-blue-800' },
  supplier_confirmed: { label: 'Supplier Confirmed', color: 'bg-indigo-100 text-indigo-800' },
  partially_delivered: { label: 'Partially Delivered', color: 'bg-purple-100 text-purple-800' },
  fully_delivered: { label: 'Fully Delivered', color: 'bg-green-100 text-green-800' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800' }
};

const PurchaseOrders = () => {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({});
  const { toast } = useToast();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchPOs();
    fetchStats();
  }, [statusFilter]);

  const fetchPOs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(
        `${API_URL}/api/procurement/purchase-orders?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to fetch POs');
      const data = await response.json();
      setPos(data);
    } catch (error) {
      console.error('Error fetching POs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load purchase orders',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/procurement/purchase-orders/stats`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <FileText className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Purchase Orders</h2>
              <p className="text-sm text-[#6C757D]">Manage purchase orders with enterprise workflow</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/admin/procurement/purchase-orders/create')}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create PO
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-[#6C757D]" />
              <span className="text-sm text-[#6C757D]">Status:</span>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="sent_to_supplier">Sent to Supplier</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Total</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{stats.total || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Draft</p>
              <p className="text-3xl font-bold text-gray-600">{stats.draft || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pending_approval || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Approved</p>
              <p className="text-3xl font-bold text-green-600">{stats.approved || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* POs List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading purchase orders...</p>
          </CardContent>
        </Card>
      ) : pos.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {statusFilter ? 'No purchase orders found for this status.' : 'No purchase orders yet. Create your first PO!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pos.map((po) => (
            <Card
              key={po.id}
              className="cursor-pointer hover:border-[#00B4D8] transition-colors"
              onClick={() => navigate(`/admin/procurement/purchase-orders/${po.id}`)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <FileText className="h-5 w-5 text-[#00B4D8]" />
                      <h3 className="text-lg font-semibold text-[#2B2D42]">
                        {po.po_number}
                      </h3>
                      {getStatusBadge(po.status)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Company</p>
                        <p className="text-sm text-[#2B2D42] font-medium">
                          {po.company_snapshot?.legal_name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Supplier</p>
                        <p className="text-sm text-[#2B2D42] font-medium">
                          {po.supplier_snapshot?.company_name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">PO Date</p>
                        <p className="text-sm text-[#2B2D42]">
                          {formatDate(po.po_date)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Total Amount</p>
                        <p className="text-sm text-[#2B2D42] font-bold">
                          {formatCurrency(po.total_amount, po.currency)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;